import { Typography, NativeSelect, Card, CardContent, CardHeader } from "@altimateai/lego";
import { Stack, Spinner } from "@altimateai/lego";
import { DataTable } from "@/components/tables";
import { Paginate } from "@/components/paginate";
import { subscriptionDetailColumns } from "./subscriptionDetailColumns";
import { StatusFilter, SubscriptionType, TIME_PERIODS } from "@/pages/subscriptions/constants";
import type { HistorySectionProps } from "./types";
import { SubscriptionHistoryItem } from "@/pages/subscriptions/services/types";
import HistoryEmptyState from "./HistoryEmptyState";

// Filter options depend on subscription type:
//   alerts -> Triggered / Skipped / Failed (no Sent — alerts don't deliver reports)
//   reports -> Sent / Failed (no Triggered — reports don't have a trigger semantic)
const ALERT_FILTER_OPTIONS = [
  { label: "All Status", value: StatusFilter.ALL },
  { label: "Triggered", value: StatusFilter.TRIGGERED },
  { label: "Skipped", value: StatusFilter.SKIPPED },
  { label: "Failed", value: StatusFilter.FAILURE },
];

const REPORT_FILTER_OPTIONS = [
  { label: "All Status", value: StatusFilter.ALL },
  { label: "Sent", value: StatusFilter.SENT },
  { label: "Failed", value: StatusFilter.FAILURE },
];

const HistorySection = ({
  statusFilter,
  currentPeriod,
  subscriptionType,
  historyData,
  historyError,
  isHistoryLoading,
  page,
  pageSize,
  numPages,
  onStatusFilterChange,
  onPeriodChange,
  onPageChange,
  onPageSizeChange,
  onResetPage,
}: HistorySectionProps) => {
  const paginatedHistory = historyData?.items || [];
  const hasHistory = paginatedHistory.length > 0;
  // Explicit branching so an unexpected subscription kind (e.g. a future enum
  // value) renders a safe minimum instead of silently picking report options.
  let filterOptions;
  if (subscriptionType === SubscriptionType.ALERT) {
    filterOptions = ALERT_FILTER_OPTIONS;
  } else if (subscriptionType === SubscriptionType.REPORT) {
    filterOptions = REPORT_FILTER_OPTIONS;
  } else {
    filterOptions = [
      { label: "All Status", value: StatusFilter.ALL },
      { label: "Failed", value: StatusFilter.FAILURE },
    ];
  }

  return (
    <Card>
      <CardHeader>
        <Typography variant="h4" className="al-text-2xl al-text-gray-900">
          History
        </Typography>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <Stack direction="row" gap="md" className="al-mb-4">
          <NativeSelect
            options={filterOptions}
            value={statusFilter}
            onChange={onStatusFilterChange}
            className="al-w-[200px]"
            size="sm"
            variant="outline"
          />

          <NativeSelect
            options={TIME_PERIODS.map(timePeriod => ({
              label: timePeriod.label,
              value: timePeriod.period.toString(),
            }))}
            value={currentPeriod.toString()}
            onChange={onPeriodChange}
            placeholder="Select time period"
            className="al-w-[180px]"
            size="sm"
            variant="outline"
          />
        </Stack>

        {/* History Table or Empty State */}
        {historyError ? (
          <Stack direction="column" align="center" justify="center" className="al-py-12">
            <Typography variant="body" className="al-text-red-500">
              Failed to load history. Please try again.
            </Typography>
          </Stack>
        ) : isHistoryLoading ? (
          <Stack direction="column" align="center" justify="center" gap="sm" className="al-py-12">
            <Spinner />
            <Typography variant="body" className="al-text-sm al-text-gray-500">
              Loading history...
            </Typography>
          </Stack>
        ) : hasHistory ? (
          <>
            <DataTable
              header={subscriptionDetailColumns}
              data={{ items: paginatedHistory as SubscriptionHistoryItem[] }}
              resetPage={onResetPage}
            />
            <Paginate
              itemCount={historyData?.total ?? 0}
              page={page}
              pageSize={pageSize}
              numPages={numPages}
              onPageClick={onPageChange}
              onPageSizeChange={onPageSizeChange}
            />
          </>
        ) : (
          <HistoryEmptyState />
        )}
      </CardContent>
    </Card>
  );
};

export default HistorySection;
